import { useState } from 'react';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { type IEscrowPartyParams } from 'src/graphql/schema';
import { DepositorUpdateDocument } from 'src/graphql/actions/depositor/depositorUpdate.mutation';
import { updateDepositorCache } from 'src/graphql/client/cache';
import { ClientError, getApiError } from 'src/utils/errors';
import {
  OrganizationForm,
  OrganizationRegistrationNumberTooltip,
  type IOrganizationForm,
} from '@/components/Organization';

import { useDepositorContext } from '../model';

const EditDepositorDialog = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { depositor, escrowId } = useDepositorContext();
  const [updateDepositor] = useMutation(DepositorUpdateDocument, {
    update: (_, { data }) => updateDepositorCache(escrowId, data?.depositorUpdate?.depositor),
  });

  const address = {
    street: depositor?.street || '',
    city: depositor?.city || '',
    postalCode: depositor?.postalCode || '',
    region: depositor?.region || '',
    country: depositor?.country || '',
  };

  const defaultValues: IOrganizationForm = {
    ...address,
    companyName: depositor?.companyName || '',
    companyWebsite: depositor?.companyWebsite || '',
    companyRegistration: depositor?.companyRegistrationNumber || '',
    postalCode: depositor?.postalCode || '',
    region: depositor?.region || '',
    streetNumber: depositor?.streetNumber || '',
  };

  const onSubmit = async ({
    companyName,
    street,
    streetNumber,
    city,
    postalCode,
    region,
    country,
    companyWebsite,
    companyRegistration,
  }: IOrganizationForm) => {
    const companyVariables: IEscrowPartyParams = {
      companyName,
      street,
      streetNumber,
      city,
      postalCode,
      region,
      country,
      companyWebsite,
      companyRegistrationNumber: companyRegistration,
    };
    try {
      const { data } = await updateDepositor({
        variables: {
          depositorId: depositor?.id ?? '',
          depositorParams: companyVariables,
        },
      });

      if (data?.depositorUpdate?.errors?.length) {
        throw new ClientError(data.depositorUpdate.errors[0]);
      } else {
        toast.success('Details updated!');
        setIsOpen(false);
      }
    } catch (e: unknown) {
      getApiError(e, toast.error);
    }
  };

  const onCancel = () => setIsOpen(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[976px] gap-0">
        <DialogHeader className="mb-6">
          <DialogTitle className="text-base font-bold">Edit organization details</DialogTitle>
          <DialogDescription className="text-xs">
            Enter the details of the organization. This includes the registered name and address of the entity, which
            will be listed in the escrow agreement.
          </DialogDescription>
        </DialogHeader>
        <OrganizationForm
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          onCancel={onCancel}
          registrationTooltip={<OrganizationRegistrationNumberTooltip />}
        />
      </DialogContent>
    </Dialog>
  );
};

export default EditDepositorDialog;
